
import React from 'react';
import ResultsCard from '@/components/results/ResultsCard';
import RadarChart from '@/components/results/RadarChart';
import GaugeChart from '@/components/results/GaugeChart';
import { Card } from '@/components/ui/card';
import { PersonalityResult } from '@/types/quiz';

interface OverviewTabProps {
  radarData: { subject: string; value: number; fullMark: number }[];
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ radarData }) => {
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <ResultsCard 
        title="Maslow's Hierarchy Map" 
        description="Your results mapped to health categories"
        delay={0.1}
      >
        <RadarChart data={radarData} />
        <div className="mt-4">
          <h3 className="font-medium mb-2">Key Insights:</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
            <li>Your social health is your strongest area</li>
            <li>Consider focusing on improving your financial wellbeing</li>
            <li>Your overall profile shows good balance across domains</li>
          </ul>
        </div>
      </ResultsCard>
      
      <ResultsCard 
        title="Health Priority Areas" 
        description="Focus on these areas for maximum impact"
        delay={0.2}
      >
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between mb-1">
              <span className="font-medium">Physical Health</span>
              <span className="text-morgenBlue">Priority 1</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-morgen-gradient h-2 rounded-full" style={{ width: '70%' }}></div>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between mb-1">
              <span className="font-medium">Financial Health</span>
              <span className="text-morgenBlue">Priority 2</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-morgen-gradient h-2 rounded-full" style={{ width: '50%' }}></div>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between mb-1">
              <span className="font-medium">Mental Health</span>
              <span className="text-morgenBlue">Priority 3</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-morgen-gradient h-2 rounded-full" style={{ width: '65%' }}></div>
            </div>
          </div>
        </div>
      </ResultsCard>
    </div>
  );
};

interface PersonalityTabProps {
  personalityResult: PersonalityResult;
}

export const PersonalityTab: React.FC<PersonalityTabProps> = ({ personalityResult }) => {
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <ResultsCard 
        title="Personality Profile" 
        description="Based on established personality frameworks"
        delay={0.1}
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-2">Your Type: {personalityResult.personality.strength}</h3>
            <p className="text-gray-600 text-sm">
              You tend to {personalityResult.personality.strength === 'Extroverted' 
                ? 'gain energy from social interactions and prefer active engagement with others.' 
                : 'recharge through solitude and prefer deeper one-on-one connections.'}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <GaugeChart value={Math.round(personalityResult.personality.score)} max={5} label="Extraversion" />
            </div>
            <div className="text-center">
              <GaugeChart value={Math.round(personalityResult.relationships.score)} max={5} label="Agreeableness" />
            </div>
          </div>
        </div>
      </ResultsCard>
      
      <ResultsCard 
        title="Emotional Patterns" 
        description="Your emotional tendencies and concerns"
        delay={0.2}
      >
        <div className="space-y-4">
          <div>
            <h3 className="font-medium mb-2">Fear Level: {personalityResult.fears.level}</h3>
            <p className="text-gray-600 text-sm">
              Your responses indicate {personalityResult.fears.level === 'High' 
                ? 'significant concerns about your adequacy and future. This is an area that may benefit from focused attention.' 
                : 'a healthy relationship with fears and concerns. You have good coping mechanisms in place.'}
            </p>
          </div>
          
          <Card className="p-4">
            <h4 className="font-medium mb-2">Key Emotional Patterns</h4>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
              <li>You tend to {personalityResult.fears.score > 3 ? 'worry about the future' : 'live in the present'}</li>
              <li>In relationships, you are {personalityResult.relationships.score > 3 ? 'trusting and open' : 'cautious and reserved'}</li>
              <li>When faced with challenges, your approach is {personalityResult.fears.score > 3 ? 'to seek support' : 'self-reliant'}</li>
            </ul>
          </Card>
        </div>
      </ResultsCard>
    </div>
  );
};

export const HealthTab: React.FC<PersonalityTabProps> = ({ personalityResult }) => {
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <ResultsCard 
        title="Health Indicators" 
        description="Current health status and risk factors"
        delay={0.1}
      >
        <div className="space-y-4">
          <div>
            <h3 className="font-medium mb-2">Overall Health Status: {personalityResult.health.status}</h3>
            <p className="text-gray-600 text-sm">
              Based on your lifestyle factors and reported habits, your health is currently in the
              "{personalityResult.health.status}" category.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-morgenBlue">
                {Math.round(personalityResult.health.score * 2)}hr
              </div>
              <div className="text-sm text-gray-500">Avg. Sleep</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-morgenBlue">
                {Math.round(personalityResult.health.score)} days
              </div>
              <div className="text-sm text-gray-500">Exercise/Week</div>
            </div>
          </div>
        </div>
      </ResultsCard>
      
      <ResultsCard 
        title="Social Determinants of Health" 
        description="Environmental and social factors affecting your health"
        delay={0.2}
      >
        <div className="space-y-4">
          <div>
            <h3 className="font-medium mb-2">SDOH Barriers: {personalityResult.sdoh.barriers}</h3>
            <p className="text-gray-600 text-sm">
              {personalityResult.sdoh.barriers === 'Low' 
                ? 'You have good access to resources and support systems that promote health.' 
                : 'There are several external factors that may be impacting your health outcomes.'}
            </p>
          </div>
          
          <Card className="p-4">
            <h4 className="font-medium mb-2">Key Factors</h4>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
              <li><span className="font-medium">Financial Security:</span> {personalityResult.sdoh.score > 3 ? 'Stable' : 'Area of concern'}</li>
              <li><span className="font-medium">Healthcare Access:</span> {personalityResult.sdoh.score > 3 ? 'Good' : 'Limited'}</li>
              <li><span className="font-medium">Social Support:</span> {personalityResult.relationships.score > 3 ? 'Strong' : 'Could be improved'}</li>
            </ul>
          </Card>
        </div>
      </ResultsCard>
    </div>
  );
};

export const RecommendationsTab: React.FC<PersonalityTabProps> = ({ personalityResult }) => {
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <ResultsCard 
        title="Personalized Recommendations" 
        description="Actions tailored to your results"
        delay={0.1}
      >
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-morgenBlue">
            <h3 className="font-medium mb-1">Priority 1: Physical Health</h3>
            <p className="text-sm text-gray-600">
              Aim for {Math.max(8, Math.round(personalityResult.health.score * 2))} hours of sleep each night and consider a sleep tracking app.
            </p>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-morgenViolet">
            <h3 className="font-medium mb-1">Priority 2: Mental Wellbeing</h3>
            <p className="text-sm text-gray-600">
              Incorporate a 10-minute mindfulness practice into your daily routine to manage stress.
            </p>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-purple-400">
            <h3 className="font-medium mb-1">Priority 3: Social Connection</h3>
            <p className="text-sm text-gray-600">
              {personalityResult.relationships.score > 3 
                ? 'Maintain your strong social connections through regular check-ins.' 
                : 'Consider joining a community group aligned with your interests.'}
            </p>
          </div>
        </div>
      </ResultsCard>
      
      <ResultsCard 
        title="Morgen Health Programs" 
        description="Structured support for your health journey"
        delay={0.2}
      >
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
            <h3 className="font-medium mb-1">Sleep Optimization Program</h3>
            <p className="text-sm text-gray-600 mb-2">
              A 4-week program to improve sleep quality and consistency.
            </p>
            <div className="text-morgenBlue text-sm font-medium">Learn more →</div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
            <h3 className="font-medium mb-1">Stress Management Workshop</h3>
            <p className="text-sm text-gray-600 mb-2">
              Interactive sessions to build resilience and coping strategies.
            </p>
            <div className="text-morgenBlue text-sm font-medium">Learn more →</div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
            <h3 className="font-medium mb-1">Financial Wellness Course</h3>
            <p className="text-sm text-gray-600 mb-2">
              Tools and strategies to improve financial health and reduce stress.
            </p>
            <div className="text-morgenBlue text-sm font-medium">Learn more →</div>
          </div>
        </div>
      </ResultsCard>
    </div>
  );
};
